import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socialQueueApi from '../services/socialQueueApi';
import { 
  Twitter, Linkedin, Facebook, Instagram, Youtube, MessageCircle, Video, Cloud, Download,
  Link2, Check, X, Loader2, AlertCircle, Plus, Trash2, ChevronDown, ChevronUp, Zap
} from 'lucide-react';
import { toast } from 'sonner';

// Platforms that support direct publishing via Composio
const COMPOSIO_PLATFORMS = [
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'bg-blue-600' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-500' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400' },
];

// Platforms that require manual export (download + copy)
const MANUAL_PLATFORMS = [
  { id: 'twitter', name: 'Twitter / X', icon: Twitter, color: 'bg-sky-500' },
  { id: 'tiktok', name: 'TikTok', icon: Video, color: 'bg-black' },
  { id: 'bluesky', name: 'Bluesky', icon: Cloud, color: 'bg-sky-400' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'bg-red-600' },
  { id: 'threads', name: 'Threads', icon: MessageCircle, color: 'bg-gray-800' },
];

const ConnectedAccounts = ({ onClose }) => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAccounts();
  }, [user]);

  const loadAccounts = async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await socialQueueApi.getConnectedAccounts(user.uid);
      setAccounts(data.accounts || []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setError('Failed to load connected accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (platformId) => {
    if (!user) return;
    setConnectingPlatform(platformId);
    try {
      const data = await socialQueueApi.getConnectUrl(user.uid, platformId);
      if (data.url) {
        // Open OAuth flow in new window
        const popup = window.open(data.url, 'Connect Platform', 'width=600,height=700');
        
        // Poll for completion
        const pollInterval = setInterval(async () => {
          if (popup?.closed) {
            clearInterval(pollInterval);
            setConnectingPlatform(null);
            // Reload accounts to check if connected
            await loadAccounts();
            toast.success(`${platformId} account connected!`);
          }
        }, 1000);
        
        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setConnectingPlatform(null);
        }, 300000);
      } else {
        toast.error(`No connection URL returned for ${platformId}`);
        setConnectingPlatform(null);
      }
    } catch (err) {
      console.error('Failed to get connect URL:', err);
      const errorMsg = err.message || '';
      if (errorMsg.includes('OAuth configuration') || errorMsg.includes('auth-configs')) {
        toast.error(`${platformId} OAuth not configured. Admin needs to set up this integration.`, {
          duration: 5000
        });
      } else {
        toast.error(`Failed to connect ${platformId}. Please try again.`);
      }
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (connectionId, platformName) => {
    if (!user || !connectionId) return;
    
    // Confirm disconnect
    if (!window.confirm(`Are you sure you want to disconnect this ${platformName} account? You'll need to reconnect to publish to it.`)) {
      return;
    }
    
    setDisconnectingId(connectionId);
    try {
      await socialQueueApi.disconnectAccount(connectionId, user.uid);
      toast.success(`${platformName} account disconnected`);
      await loadAccounts();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      toast.error(`Failed to disconnect ${platformName}. Please try again.`);
    } finally {
      setDisconnectingId(null);
    }
  };

  // Group accounts by platform
  const getAccountsForPlatform = (platformId) => {
    return accounts.filter(a => a.platform === platformId && a.connected);
  };

  const getTotalConnectedCount = () => {
    return accounts.filter(a => a.connected).length;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-brand-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-orange/10 rounded-lg">
              <Link2 className="w-5 h-5 text-brand-orange" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Connect Platforms</h2>
              <p className="text-sm text-brand-gray-400">
                {getTotalConnectedCount() > 0 
                  ? `${getTotalConnectedCount()} account${getTotalConnectedCount() > 1 ? 's' : ''} connected`
                  : 'Link your social accounts to publish directly'
                }
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-brand-gray-400 hover:text-white hover:bg-brand-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Info banner */}
          <div className="mb-6 p-4 bg-brand-orange/10 border border-brand-orange/30 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-brand-orange font-medium mb-1">Powered by Composio</p>
              <p className="text-brand-gray-400">
                Connect multiple accounts per platform. Your credentials are securely handled by Composio.
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-orange animate-spin" />
            </div>
          )}

          {/* Platform list */}
          {!isLoading && (
            <div className="space-y-6">
              {/* Composio-enabled platforms - Direct Publish */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-400">
                  <Zap className="w-4 h-4" />
                  <p className="text-xs font-bold uppercase tracking-wider">Direct Publish</p>
                </div>
                <p className="text-xs text-brand-gray-500 mb-3">Connect to publish directly from FlowGen</p>
                
                {COMPOSIO_PLATFORMS.map(platform => {
                  const platformAccounts = getAccountsForPlatform(platform.id);
                  const hasAccounts = platformAccounts.length > 0;
                  const isConnecting = connectingPlatform === platform.id;
                  const isExpanded = expandedPlatform === platform.id;
                  const Icon = platform.icon;

                  return (
                    <div 
                      key={platform.id}
                      className={`rounded-xl border transition-all ${
                        hasAccounts 
                          ? 'bg-green-900/10 border-green-800/50' 
                          : 'bg-brand-gray-800/50 border-brand-gray-700 hover:border-green-500/50'
                      }`}
                    >
                      {/* Platform header */}
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${platform.color}`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{platform.name}</p>
                            {hasAccounts && (
                              <p className="text-xs text-green-400">
                                {platformAccounts.length} account{platformAccounts.length > 1 ? 's' : ''} connected
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {hasAccounts && (
                            <button
                              onClick={() => setExpandedPlatform(isExpanded ? null : platform.id)}
                              className="p-1.5 text-brand-gray-400 hover:text-white hover:bg-brand-gray-700 rounded-lg"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleConnect(platform.id)}
                            disabled={isConnecting}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg disabled:opacity-50 ${
                              hasAccounts 
                                ? 'bg-brand-gray-700 hover:bg-brand-gray-600 text-white' 
                                : 'bg-green-600 hover:bg-green-500 text-white'
                            }`}
                          >
                            {isConnecting ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Plus className="w-3 h-3" />
                            )}
                            {hasAccounts ? 'Add Account' : 'Connect'}
                          </button>
                        </div>
                      </div>

                      {/* Connected accounts list */}
                      {hasAccounts && isExpanded && (
                        <div className="px-4 pb-4 space-y-2">
                          {platformAccounts.map((account, idx) => (
                            <div 
                              key={account.id || idx}
                              className="flex items-center justify-between p-3 bg-brand-gray-800/50 rounded-lg"
                            >
                              <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-400" />
                                <span className="text-sm text-white">
                                  {account.account_name ? `@${account.account_name}` : `Account ${idx + 1}`}
                                </span>
                              </div>
                              <button
                                onClick={() => handleDisconnect(account.id, platform.name)}
                                disabled={disconnectingId === account.id}
                                className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg disabled:opacity-50"
                                title="Disconnect account"
                              >
                                {disconnectingId === account.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {hasAccounts && !isExpanded && platformAccounts[0]?.account_name && (
                        <div className="px-4 pb-3">
                          <p className="text-xs text-brand-gray-400">
                            @{platformAccounts[0].account_name}
                            {platformAccounts.length > 1 && ` +${platformAccounts.length - 1} more`}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Manual platforms - Download & Copy */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-brand-gray-400">
                  <Download className="w-4 h-4" />
                  <p className="text-xs font-bold uppercase tracking-wider">Download & Copy</p>
                </div>
                <p className="text-xs text-brand-gray-500 mb-3">Download images and copy captions to post manually</p>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {MANUAL_PLATFORMS.map(platform => {
                    const Icon = platform.icon;
                    return (
                      <div 
                        key={platform.id}
                        className="p-3 rounded-lg bg-brand-gray-800/30 border border-brand-gray-700/50 flex items-center gap-2"
                      >
                        <div className={`p-1.5 rounded ${platform.color}`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm text-brand-gray-400">{platform.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-brand-gray-700 bg-brand-gray-800/50">
          <p className="text-xs text-brand-gray-500 text-center">
            LinkedIn, Facebook & Instagram support direct publishing. Other platforms require download & copy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConnectedAccounts;
